import { useCallback, useEffect, useState } from 'react';

import type { ProjectConfigGitRepo } from '@/api/config-git-repo';
import { getConfigGitRepo, saveConfigGitRepo } from '@/api/config-git-repo';
import { ApiError } from '@/modules/api/api-client';

// --- Sub-components ---

function LoadingSkeleton() {
  return (
    <div className="animate-pulse space-y-4">
      <div className="h-8 w-48 rounded-lg bg-gray-200 dark:bg-gray-700" />
      <div className="h-10 rounded-lg bg-gray-200 dark:bg-gray-700" />
      <div className="h-10 rounded-lg bg-gray-200 dark:bg-gray-700" />
      <div className="h-24 rounded-lg bg-gray-200 dark:bg-gray-700" />
    </div>
  );
}

function Toggle({
  checked,
  onChange,
  id,
  label,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  id: string;
  label: string;
}) {
  return (
    <label htmlFor={id} className="inline-flex items-center gap-2 cursor-pointer">
      <span className="text-sm font-medium text-content">{label}</span>
      <button
        id={id}
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-2 ${
          checked ? 'bg-brand-700' : 'bg-gray-300 dark:bg-gray-600'
        }`}
      >
        <span
          className={`inline-block h-3.5 w-3.5 rounded-full bg-white transition-transform ${
            checked ? 'translate-x-4.5' : 'translate-x-0.5'
          }`}
        />
      </button>
    </label>
  );
}

function TextField({
  id,
  label,
  value,
  onChange,
  placeholder,
  hint,
  disabled,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  hint?: string;
  disabled: boolean;
}) {
  return (
    <div>
      <label htmlFor={id} className="block text-xs font-medium text-content-secondary mb-1">
        {label}
      </label>
      <input
        id={id}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        className="w-full rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-content focus:outline-none focus:ring-2 focus:ring-brand-600 disabled:opacity-50 disabled:cursor-not-allowed"
      />
      {hint && <p className="mt-1 text-xs text-content-tertiary">{hint}</p>}
    </div>
  );
}

// --- Main Component ---

interface ConfigGitRepoSettingsProps {
  projectKey: string;
}

export function ConfigGitRepoSettings({ projectKey }: ConfigGitRepoSettingsProps) {
  // --- State ---
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [enabled, setEnabled] = useState(false);
  const [repoUrl, setRepoUrl] = useState('');
  const [repoName, setRepoName] = useState('');
  const [branch, setBranch] = useState('main');
  const [checkoutLocation, setCheckoutLocation] = useState('');
  const [currentPath, setCurrentPath] = useState('current/');
  const [releasesPath, setReleasesPath] = useState('releases/');
  const [configurationSubPath, setConfigurationSubPath] = useState('configuration');

  // --- Load config ---
  const loadConfig = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const config = await getConfigGitRepo(projectKey);
      setEnabled(config.enabled);
      setRepoUrl(config.repoUrl ?? '');
      setRepoName(config.repoName ?? '');
      setBranch(config.branch || 'main');
      setCheckoutLocation(config.checkoutLocation ?? '');
      setCurrentPath(config.currentPath);
      setReleasesPath(config.releasesPath);
      setConfigurationSubPath(config.configurationSubPath);
    } catch (err) {
      if (err instanceof ApiError) {
        setLoadError(`Failed to load config git repository settings: ${err.message}`);
      } else {
        setLoadError('Unable to connect to server. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [projectKey]);

  useEffect(() => {
    if (projectKey) {
      loadConfig();
    }
  }, [projectKey, loadConfig]);

  // --- Save config ---
  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const config: ProjectConfigGitRepo = {
        projectKey,
        repoUrl: repoUrl.trim() || null,
        repoName: repoName.trim() || null,
        branch: branch.trim() || 'main',
        checkoutLocation: checkoutLocation.trim() || null,
        currentPath: currentPath.trim() || 'current/',
        releasesPath: releasesPath.trim() || 'releases/',
        configurationSubPath: configurationSubPath.trim() || 'configuration',
        enabled,
      };
      const saved = await saveConfigGitRepo(projectKey, config);
      // Reflect any server-side normalisation (e.g. trailing slashes).
      setRepoUrl(saved.repoUrl ?? '');
      setRepoName(saved.repoName ?? '');
      setBranch(saved.branch || 'main');
      setCheckoutLocation(saved.checkoutLocation ?? '');
      setCurrentPath(saved.currentPath);
      setReleasesPath(saved.releasesPath);
      setConfigurationSubPath(saved.configurationSubPath);
      setEnabled(saved.enabled);
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { message?: string } | undefined;
        setSaveError(body?.message ?? err.message);
      } else {
        setSaveError('Unable to connect to server. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  // --- Render: Load error state ---
  if (loadError && !loading) {
    return (
      <section className="mt-6">
        <h3 className="text-sm font-semibold text-content mb-3">Config Git Repository</h3>
        <div role="alert" className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-4">
          <p className="text-sm text-red-700 dark:text-red-300">{loadError}</p>
          <button
            type="button"
            onClick={loadConfig}
            className="mt-2 rounded-md border border-border px-3 py-1.5 text-xs font-medium text-content-secondary hover:bg-surface-raised focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-2"
          >
            Retry
          </button>
        </div>
      </section>
    );
  }

  // --- Render: Loading state ---
  if (loading) {
    return (
      <section className="mt-6">
        <h3 className="text-sm font-semibold text-content mb-3">Config Git Repository</h3>
        <LoadingSkeleton />
      </section>
    );
  }

  // --- Render: Main form ---
  return (
    <section className="mt-6 space-y-4">
      {/* Header with master toggle */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-content">Config Git Repository</h3>
          <p className="mt-1 text-xs text-content-secondary">
            The git repository that stores this project&apos;s configuration files. Corvus checks
            out this repository during config-file deployments to resolve source files.
          </p>
        </div>
        <Toggle
          id="config-git-repo-master-toggle"
          checked={enabled}
          onChange={setEnabled}
          label={enabled ? 'Enabled' : 'Disabled'}
        />
      </div>

      {/* Disabled banner */}
      {!enabled && (
        <div
          role="status"
          className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-3"
        >
          <p className="text-xs text-amber-700 dark:text-amber-300 font-medium">
            Config git repository is disabled. Enable it and set a repository URL to deploy
            configuration files.
          </p>
        </div>
      )}

      {/* Save error banner */}
      {saveError && (
        <div role="alert" className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-3">
          <p className="text-sm text-red-700 dark:text-red-300">{saveError}</p>
        </div>
      )}

      {/* Repository fields */}
      <fieldset
        className={`rounded-lg border border-border p-4 space-y-3 ${enabled ? '' : 'opacity-60'}`}
        disabled={!enabled}
      >
        <legend className="text-sm font-semibold text-content px-1">Repository</legend>

        <TextField
          id="config-git-repo-url"
          label="Repository URL"
          value={repoUrl}
          onChange={setRepoUrl}
          placeholder="git@example.com:team/project-config-files.git"
          hint="The clone URL used to check out configuration files on target servers."
          disabled={!enabled}
        />

        <TextField
          id="config-git-repo-checkout-location"
          label="Checkout Location"
          value={checkoutLocation}
          onChange={setCheckoutLocation}
          placeholder="/opt/corvus/project-config-files"
          hint="Path on the target server where the repository is checked out."
          disabled={!enabled}
        />

        <TextField
          id="config-git-repo-name"
          label="CodeCommit Repository Name"
          value={repoName}
          onChange={setRepoName}
          placeholder="project-config-files"
          hint="CodeCommit repository name used to list available versions (no checkout required)."
          disabled={!enabled}
        />

        <TextField
          id="config-git-repo-branch"
          label="Branch"
          value={branch}
          onChange={setBranch}
          placeholder="main"
          hint="Branch inspected when listing available versions."
          disabled={!enabled}
        />
      </fieldset>

      {/* Path layout fields */}
      <fieldset
        className={`rounded-lg border border-border p-4 space-y-3 ${enabled ? '' : 'opacity-60'}`}
        disabled={!enabled}
      >
        <legend className="text-sm font-semibold text-content px-1">Path Layout</legend>
        <p className="text-xs text-content-tertiary">
          Source files resolve as{' '}
          <code className="font-mono">{'{versionPath}/{configurationSubPath}/{sourceRoot}/{sourceLocation}/{sourceFilename}'}</code>.
          The version path is either the Current or Releases prefix below.
        </p>

        <TextField
          id="config-git-repo-current-path"
          label="Current Path"
          value={currentPath}
          onChange={setCurrentPath}
          placeholder="current/"
          hint="Path prefix for the working version (the “current” option)."
          disabled={!enabled}
        />

        <TextField
          id="config-git-repo-releases-path"
          label="Releases Path"
          value={releasesPath}
          onChange={setReleasesPath}
          placeholder="releases/"
          hint="Path prefix for tagged releases; the selected version is appended (e.g. releases/v2.1.0/)."
          disabled={!enabled}
        />

        <TextField
          id="config-git-repo-configuration-sub-path"
          label="Configuration Sub-path"
          value={configurationSubPath}
          onChange={setConfigurationSubPath}
          placeholder="configuration"
          hint="Sub-directory under the version path that contains the source-root folders."
          disabled={!enabled}
        />
      </fieldset>

      {/* Save button */}
      <div className="pt-2">
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="rounded-md bg-brand-700 px-4 py-2 text-sm font-medium text-white hover:bg-brand-800 focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {saving ? 'Saving...' : 'Save'}
        </button>
      </div>
    </section>
  );
}
